"use client";

import { motion, type Transition } from "motion/react";
import type * as React from "react";

export interface GradientTextProps
	extends Omit<React.ComponentProps<"span">, "children"> {
	text: string;
	gradient?: string;
	fromColor?: string;
	viaColor?: string;
	toColor?: string;
	neon?: boolean;
	transition?: Transition;
}

export function GradientText({
	text,
	style,
	gradient,
	fromColor = "#3b82f6",
	viaColor = "#a855f7",
	toColor = "#ec4899",
	neon = false,
	transition = {
		duration: 50,
		repeat: Number.POSITIVE_INFINITY,
		ease: "linear",
	},
	...props
}: GradientTextProps) {
	const gradientString =
		gradient ||
		`linear-gradient(90deg, ${fromColor} 0%, ${viaColor} 20%, ${toColor} 50%, ${viaColor} 80%, ${fromColor} 100%)`;

	const baseStyle: React.CSSProperties = {
		backgroundImage: gradientString,
		margin: 0,
		color: "transparent",
		backgroundClip: "text",
		backgroundSize: "700% 100%",
		backgroundPosition: "0% 0%",
	};

	return (
		<span
			data-slot="gradient-text"
			style={{ position: "relative", display: "inline", ...style }}
			{...props}
		>
			<motion.span
				style={baseStyle}
				initial={{ backgroundPosition: "0% 0%" }}
				animate={{ backgroundPosition: "500% 100%" }}
				transition={transition}
			>
				{text}
			</motion.span>

			{neon && (
				<motion.span
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						mixBlendMode: "plus-lighter",
						filter: "blur(8px)",
						...baseStyle,
					}}
					initial={{ backgroundPosition: "0% 0%" }}
					animate={{ backgroundPosition: "500% 100%" }}
					transition={transition}
				>
					{text}
				</motion.span>
			)}
		</span>
	);
}
